import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface DispatchProgress {
  status: 'pending' | 'partially_dispatched' | 'dispatched' | 'delivered';
  items: { description: string; unit: string; quantity: number; dispatchedQuantity: number; pendingQuantity: number }[];
  requiredDispatchDate?: string | null;
  isOverdue?: boolean;
  overdueDays?: number;
  handoffStatus?: string;
}
export interface SalesOrder { dispatchProgress?: DispatchProgress; _id: string; orderNumber: string; documentType: string; date: string; customerId: any; customerName: string; status: string; grandTotal: number; taxTotal?: number; amountPaid: number; lines: any[]; createdBy: any; customerPoNumber?: string; customerPoDate?: string; csatScore?: number | null; csatFeedback?: string; }
export interface SaleReturn { _id: string; returnNumber: string; salesOrderId: any; date: string; totalReturned: number; refundAmount: number; status: string; }
export interface Shipment { _id: string; shipmentNumber: string; salesOrderId: any; date: string; status: string; trackingNumber: string; carrier: string; }

export function useSalesOrders(filters?: { status?: string; documentType?: string; productId?: string; dispatchOverdue?: boolean; limit?: number }) {
  return useQuery({
    queryKey: ['sales-ops', 'orders', filters],
    refetchInterval: 15_000,
    queryFn: async () => {
      const p = new URLSearchParams();
      if (filters?.status) p.append('status', filters.status);
      if (filters?.documentType) p.append('documentType', filters.documentType);
      if (filters?.productId) p.append('productId', filters.productId);
      if (filters?.dispatchOverdue) p.append('dispatchOverdue', 'true');
      p.append('limit', String(filters?.limit || 500));
      const { data } = await api.get(`/api/sales-ops/orders?${p}`);
      return data as { data: SalesOrder[]; pagination: any };
    },
  });
}

export interface TopSellingProduct {
  productId: string | null;
  productName: string;
  productCode: string;
  category: string;
  unit: string;
  packSize: string;
  totalQuantity: number;
  totalAmount: number;
  orderCount: number;
}

export function useTopSellingProducts(params?: { fromDate?: string; toDate?: string; limit?: number; scope?: string }) {
  return useQuery({
    queryKey: ['sales-ops', 'top-products', params],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (params?.fromDate) p.append('fromDate', params.fromDate);
      if (params?.toDate) p.append('toDate', params.toDate);
      if (params?.limit) p.append('limit', String(params.limit));
      if (params?.scope) p.append('scope', params.scope);
      const { data } = await api.get<{ data: TopSellingProduct[] }>(`/api/sales-ops/orders/top-products?${p}`);
      return data.data ?? [];
    },
    staleTime: 60_000,
  });
}
export function useSaleReturns() { return useQuery({ queryKey: ['sales-ops', 'returns'], queryFn: async () => { const { data } = await api.get('/api/sales-ops/returns'); return data as { data: SaleReturn[]; pagination: any }; } }); }
export function useShipments() { return useQuery({ queryKey: ['sales-ops', 'shipments'], queryFn: async () => { const { data } = await api.get('/api/sales-ops/shipments'); return data as { data: Shipment[]; pagination: any }; } }); }
export function useCreateShipment() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: { salesOrderId: string; lines: any[]; trackingNumber?: string; carrier?: string; locationId?: string | null }) => { const { data } = await api.post('/api/sales-ops/shipments', payload); return data as Shipment; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops'] }) }); }
export function useCommissionAccruals() { return useQuery({ queryKey: ['sales-ops', 'commissions'], queryFn: async () => { const { data } = await api.get('/api/sales-ops/commissions/accruals'); return data as { data: any[]; totalCommission: number; pagination: any }; } }); }

// ── Cross-sell / Upsell ──────────────────────────────────────────────────────
// "Customers who bought these also bought…" — computed live from real order
// history (src/api-sales/orders.js's /related-products aggregation), never a
// hand-curated list.
export interface RelatedProduct {
  productId: string;
  coOccurrences: number;
  avgUnitPrice: number;
  product: { _id: string; name: string; sku: string; sellingPrice: number; taxProfileId?: { cgstRate?: number; sgstRate?: number; igstRate?: number } };
}

export function useRelatedProducts(orderId: string | null) {
  return useQuery({
    queryKey: ['sales-ops', 'related-products', orderId],
    queryFn: async () => {
      const { data } = await api.get<{ data: RelatedProduct[] }>(`/api/sales-ops/orders/${orderId}/related-products`);
      return data.data;
    },
    enabled: !!orderId,
  });
}

export interface SalesCustomerOption { id: string; name: string; phone: string; email: string }

/** Customer lookup for the sales-order form (sales-scoped, no Accounts access needed). */
export function useSalesCustomers(enabled = true) {
  return useQuery({
    queryKey: ['sales-ops', 'customers'],
    queryFn: async () => (await api.get<SalesCustomerOption[]>('/api/sales-ops/customers')).data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSalesOrder() { const qc = useQueryClient(); return useMutation({ mutationFn: async (payload: any) => { const { data } = await api.post('/api/sales-ops/orders', payload); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops'] }) }); }
export function useSalesOrderAction() { const qc = useQueryClient(); return useMutation({ mutationFn: async ({ id, action, body }: { id: string; action: string; body?: any }) => { const { data } = await api.post(`/api/sales-ops/orders/${id}/${action}`, body); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops'] }) }); }
export function useConvertSOtoPO() { const qc = useQueryClient(); return useMutation({ mutationFn: async (id: string) => { const { data } = await api.post(`/api/sales-ops/orders/${id}/convert-to-po`); return data as { ok: boolean; purchaseOrders: any[]; shortfallLines: number; unassignedProducts?: string[] }; }, onSuccess: () => { qc.invalidateQueries({ queryKey: ['sales-ops'] }); qc.invalidateQueries({ queryKey: ['procurement'] }); } }); }
export function useShipmentAction() { const qc = useQueryClient(); return useMutation({ mutationFn: async ({ id, action, body }: { id: string; action: string; body?: any }) => { const { data } = await api.post(`/api/sales-ops/shipments/${id}/${action}`, body); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops'] }) }); }
export function useReturnAction() { const qc = useQueryClient(); return useMutation({ mutationFn: async ({ id, action }: { id: string; action: string }) => { const { data } = await api.post(`/api/sales-ops/returns/${id}/${action}`); return data; }, onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops'] }) }); }

// ── Credit Notes ─────────────────────────────────────────────────────────────

export interface CreditNote {
  _id: string;
  noteNumber: string;
  date: string;
  customerId: any;
  invoiceId: any;
  reason: string;
  grandTotal: number;
  status: string;
  createdBy: any;
}

export function useCreditNotes() {
  return useQuery({
    queryKey: ['sales-ops', 'credit-notes'],
    queryFn: async () => { const { data } = await api.get('/api/sales-ops/credit-notes'); return data as { data: CreditNote[]; pagination: any }; },
  });
}

// ── Repeat Orders (recurring 'order'-kind profiles) ─────────────────────────
// Backed by the same recurring-profile engine Accounts uses for recurring
// invoices/bills (src/api-accounts/recurring-profiles.js) — a Sales caller
// may only create/see/manage profiles of kind 'order' that they created
// themselves; the backend enforces that, this is just the Sales-side view.

export interface RepeatOrderProfile {
  _id: string;
  name: string;
  kind: 'order';
  frequency: string;
  interval: number;
  nextRunAt: string;
  lastRunAt?: string;
  status: 'active' | 'paused' | 'completed' | 'failed';
  runCount: number;
  lastFailure?: string;
}

export function useRepeatOrders() {
  return useQuery({
    queryKey: ['sales-ops', 'repeat-orders'],
    queryFn: async () => {
      const { data } = await api.get<RepeatOrderProfile[]>('/api/accounts/recurring-profiles', { params: { kind: 'order' } });
      return Array.isArray(data) ? data : [];
    },
  });
}

export function useCreateRepeatOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      name: string; frequency: string; interval: number; startDate: string; endDate?: string | null;
      template: { customerId: string; locationId?: string | null; paymentTerms?: string; lineItems: any[] };
    }) => {
      const { data } = await api.post('/api/accounts/recurring-profiles', { kind: 'order', ...payload });
      return data as RepeatOrderProfile;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops', 'repeat-orders'] }),
  });
}

export function useRepeatOrderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'pause' | 'resume' | 'retry' }) => {
      const { data } = await api.post(`/api/accounts/recurring-profiles/${id}/${action}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sales-ops', 'repeat-orders'] }),
  });
}
